import { router } from '@inertiajs/react'
import { Dropdown } from 'bootstrap'
import { useEffect, useMemo, useRef, useState } from 'react'

import { titleize } from '../../../utils/string'

const EMPLOYMENT_STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'probationary', label: 'Probationary' },
  { key: 'permanent', label: 'Permanent' },
  { key: 'contractual', label: 'Contractuals' },
  { key: 'professional', label: 'Professional' },
]

function employeePath(employeeId) {
  return `/company_settings/employees/${employeeId}`
}

function employeeEditPath(employeeId) {
  return `/company_settings/employees/${employeeId}/edit`
}

function normalizeForSearch(value) {
  return value == null ? '' : String(value).trim().toLowerCase()
}

function getEmploymentStatusKey(employee) {
  return normalizeForSearch(employee?.employment_status)
}

function getEmploymentStatusCounts(employees, tabs) {
  const list = employees || []
  const keys = tabs.map((t) => t.key).filter((k) => k !== 'all')

  const counts = { all: list.length }
  keys.forEach((k) => {
    counts[k] = 0
  })

  list.forEach((u) => {
    if (!u?.supervisor_id && Object.prototype.hasOwnProperty.call(counts, 'unassigned')) {
      counts.unassigned += 1
    }

    const s = getEmploymentStatusKey(u)
    if (!s) return
    if (Object.prototype.hasOwnProperty.call(counts, s)) counts[s] += 1
  })

  return counts
}

function getEmployeeDisplayName(employee) {
  const fullName = employee?.full_name
  if (fullName) return String(fullName).trim()

  const first = employee?.first_name ? String(employee.first_name).trim() : ''
  const last = employee?.last_name ? String(employee.last_name).trim() : ''
  const combined = [first, last].filter(Boolean).join(' ').trim()
  if (combined) return combined

  return employee?.email ? String(employee.email).trim() : ''
}

function getEmployeeSearchHaystack(employee) {
  const parts = []
  if (employee?.first_name) parts.push(employee.first_name)
  if (employee?.last_name) parts.push(employee.last_name)
  if (employee?.full_name) parts.push(employee.full_name)
  if (employee?.email) parts.push(employee.email)
  if (employee?.roles?.length) parts.push(employee.roles.join(' '))
  if (employee?.phone) parts.push(employee.phone)
  if (employee?.birthday) parts.push(employee.birthday)

  const normalized = []
  parts.forEach((p) => {
    const v = normalizeForSearch(p)
    if (v) normalized.push(v)
  })
  return normalized.join(' ')
}

function filterEmployees({ employees, query, statusFilter }) {
  let list = employees || []

  if (statusFilter && statusFilter !== 'all') {
    if (statusFilter === 'unassigned') {
      list = list.filter((u) => typeof getEmploymentStatusKey(u) === 'string' && getEmploymentStatusKey(u) !== '');
    } else {
      list = list.filter((u) => getEmploymentStatusKey(u) === statusFilter)
    }
  }

  const q = normalizeForSearch(query)
  if (!q) return list

  return list.filter((u) => getEmployeeSearchHaystack(u).includes(q))
}

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

function RolesBadges({ roles }) {
  const list = (roles || []).length ? roles : ['user']
  return (
    <div className="d-flex flex-wrap gap-1">
      {list.map((r) => (
        <span key={r} className="badge text-bg-secondary-subtle">
          {titleize(r).toUpperCase()}
        </span>
      ))}
    </div>
  )
}

function EmployeeNameLink({ employee, onClick }) {
  const name = getEmployeeDisplayName(employee)
  const href = employeePath(employee.id)

  return (
    <a
      className="text-reset fw-semibold"
      href={href}
      onClick={(e) => {
        e.preventDefault()
        onClick?.()
      }}
    >
      {name}
    </a>
  )
}

function EmployeeCell({ employee, onClick }) {
  return (
    <div className="d-flex align-items-center">
      <button type="button" className="avatar avatar-sm me-3 btn p-0 border-0" onClick={onClick}>
        <img src={employee.avatar_url} className="avatar-img rounded-circle" alt={employee.full_name || employee.email} />
      </button>
      <div>
        <EmployeeNameLink employee={employee} onClick={onClick} />
        <div className="small text-body-secondary">{employee.email || ''}</div>
      </div>
    </div>
  )
}

function EmployeeRow({ employee, onShow, onEdit }) {
  const statusKey = getEmploymentStatusKey(employee)
  const statusLabel = statusKey ? titleize(statusKey) : '—'

  return (
    <tr>
      <td>
        <EmployeeCell employee={employee} onClick={onShow} />
      </td>
      <td>
        <RolesBadges roles={employee.roles} />
      </td>
      <td className="text-body-secondary">{statusLabel}</td>
      <td className="text-body-secondary">{employee.phone || ''}</td>
      <td className="text-body-secondary">{employee.birthday || ''}</td>
      <td className="text-end">
        <div className="dropdown">
          <button
            className="btn btn-sm btn-white"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            data-employee-actions-toggle
          >
            <i className="fe fe-more-vertical" />
          </button>
          <div className="dropdown-menu dropdown-menu-end">
            <button type="button" className="dropdown-item" onClick={onEdit}>
              Edit user
            </button>
          </div>
        </div>
      </td>
    </tr>
  )
}

export default function Index({ employees, notice, alert }) {
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [flashNotice, setFlashNotice] = useState(notice)
  const [flashAlert, setFlashAlert] = useState(alert)
  const didReload = useRef(false)

  useEffect(() => {
    if (notice) setFlashNotice(notice)
    if (alert) setFlashAlert(alert)
  }, [notice, alert])

  useEffect(() => {
    if (!notice || didReload.current) return
    didReload.current = true
    router.reload({ only: ['employees'], preserveScroll: true, preserveState: true })
  }, [notice])

  const statusCounts = useMemo(() => getEmploymentStatusCounts(employees, EMPLOYMENT_STATUS_TABS), [employees])
  const filtered = useMemo(
    () => filterEmployees({ employees, query, statusFilter }),
    [employees, query, statusFilter],
  )

  useEffect(() => {
    const toggles = document.querySelectorAll('[data-employee-actions-toggle]')
    const handlers = []

    toggles.forEach((toggle) => {
      const handler = (event) => {
        event.preventDefault()
        Dropdown.getOrCreateInstance(toggle).toggle()
      }

      toggle.addEventListener('click', handler)
      handlers.push({ toggle, handler })
    })

    return () => {
      handlers.forEach(({ toggle, handler }) => {
        toggle.removeEventListener('click', handler)
      })
    }
  }, [filtered])

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Human Resources</h6>
              <h1 className="header-title text-truncate">Employees</h1>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col">
              <ul className="nav nav-tabs nav-overflow header-tabs">
                {EMPLOYMENT_STATUS_TABS.map((t) => (
                  <li key={t.key} className="nav-item">
                    <a
                      href="#"
                      className={`nav-link text-nowrap ${statusFilter === t.key ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        setStatusFilter(t.key)
                      }}
                    >
                      {t.label}{' '}
                      <span className="badge rounded-pill text-bg-secondary-subtle">{statusCounts[t.key] || 0}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />
      <FlashAlert variant="success" message={flashNotice} textDark onDismiss={() => setFlashNotice(null)} />

      <div className="card" id="employeesList">
        <div className="card-header">
          <div className="row align-items-center">
            <div className="col">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="input-group input-group-flush input-group-merge input-group-reverse">
                  <input
                    className="form-control"
                    type="search"
                    placeholder="Search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <span className="input-group-text">
                    <i className="fe fe-search" />
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-hover table-nowrap card-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Roles</th>
                <th>Employment Status</th>
                <th>Phone</th>
                <th>Birthday</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody className="list fs-base">
              {filtered.map((u) => (
                <EmployeeRow
                  key={u.id}
                  employee={u}
                  onShow={() => router.visit(employeePath(u.id))}
                  onEdit={() => router.visit(employeeEditPath(u.id))}
                />
              ))}

              {!filtered.length ? (
                <tr>
                  <td colSpan={6} className="text-center text-body-secondary py-4">
                    No employees found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
